// Production features the Flutter console had and the React console restores (parity
// audit 2026-09-30). Every route here is SERVED by the back end today (router.ts under
// /api/aithora, or server.ts under /api); none carries the `real` flag, because none of
// them reports a figure: they are actions and plain lists.
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

// ── Web push (router.ts /devices/register, /devices/unregister) ─────────────

/** POST /devices/register {token, platform}: the session user's own browser. */
export const deviceRegister = route<{ ok: boolean }, void, { token: string; platform: 'web'; name?: string; replaces?: string }>()('POST', `${A}/devices/register`, { readOnlySafe: true });
/** POST /devices/unregister {token}: on sign-out, so a signed-out browser gets no push. */
export const deviceUnregister = route<{ ok: boolean }, void, { token: string }>()('POST', `${A}/devices/unregister`, { readOnlySafe: true });

// ── Notification Center (router.ts /notifications/:id/*) ───────────────────

/** The alert row after an acknowledge, snooze or resolve (notifications/center.ts). */
export interface NotificationItem {
  id: string;
  status: string;
  acknowledgedAt: string | null;
  snoozedUntil: string | null;
  resolvedAt: string | null;
}
/** POST /notifications/:id/acknowledge */
export const notificationAcknowledge = route<NotificationItem>()('POST', `${A}/notifications/:id/acknowledge`);
/** POST /notifications/:id/snooze {minutes}: 60 by default, 1440 at most. */
export const notificationSnooze = route<NotificationItem, void, { minutes: number }>()('POST', `${A}/notifications/:id/snooze`);
/** POST /notifications/:id/resolve */
export const notificationResolve = route<NotificationItem>()('POST', `${A}/notifications/:id/resolve`);

// ── Emergency response (router.ts /emergency/*, /crisis-resources) ──────────

/** One person on an incident's escalation roster (emergency.ts getIncidentContacts). */
export interface IncidentContact {
  recipientId: string;
  name: string | null;
  role: string | null;
  title: string | null;
  severity: string | null;
  notifiedAt: string | null;
  notified: boolean;
}
/** GET /emergency/incidents/:id/contacts: who was paged, and who could be. */
export const incidentContacts = route<{ contacts: IncidentContact[] }>()('GET', `${A}/emergency/incidents/:id/contacts`);
/** What a staff "Notify" will send (AF-1611): recipient, relationship, channel and the exact text. */
export interface IncidentNotifyPreview {
  recipient: { id: string; name: string | null; relationship: string; channel: string };
  text: string;
}
/** GET /emergency/notify-contact/preview?incidentId=&contactId=: the one confirmation sheet's content. */
export const incidentNotifyPreview = route<IncidentNotifyPreview, { incidentId: string; contactId: string }>()('GET', `${A}/emergency/notify-contact/preview`);
/** POST /emergency/notify-contact {incidentId, contactId, confirmed: true, text}: send it after the sheet. Without confirmed: 428; a changed text: 409. */
export const incidentNotifyContact = route<{ ok: boolean; notifiedByUserId: string | null; text?: string }, void, { incidentId: string; contactId: string; confirmed: true; text: string }>()(
  'POST', `${A}/emergency/notify-contact`);

/** One crisis line (providers.ts CrisisIntegration.SERVICES). */
export interface CrisisResource {
  name: string;
  phone: string;
  text: string;
  url: string;
}
export type CrisisType = 'SELF_HARM' | 'ABUSE' | 'EXPLOITATION' | 'FRAUD';
/** GET /crisis-resources?type=: the lines for a kind of emergency (988, the elder abuse line to APS). */
export const crisisResources = route<{ resources: CrisisResource[] }, { type: CrisisType }>()('GET', `${A}/crisis-resources`);

// ── Atlas autonomy governance (server.ts /api/atlas/autonomy/*, /api/stops) ─

export type AutonomyLevelName = 'observe' | 'suggest' | 'assist' | 'full';

/** One pending two-person request to raise autonomy to FULL. */
export interface AutonomyProposal {
  id: string;
  kind: 'autonomy' | 'ceiling';
  summary: string;
  targetLevel: AutonomyLevelName;
  requiredApprovals: number;
  approvalsCount: number;
  expiresAt: string;
  /** This caller may sign it now (a distinct signer; for a facility's FULL, the owner). */
  canApprove: boolean;
  isProposer: boolean;
  /** The server restarted since it was proposed: it can no longer be applied. */
  stale: boolean;
}
/** GET /api/atlas/autonomy/proposals */
export const autonomyProposals = route<{ proposals: AutonomyProposal[] }>()('GET', '/api/atlas/autonomy/proposals');
/** POST /api/atlas/autonomy/proposals/:id/approve: a facility's FULL. The owner only. */
export const autonomyProposalApprove = route<{ approved?: boolean; status?: string; approvalsCount?: number; requiredApprovals?: number }, void, { reason?: string }>()(
  'POST', '/api/atlas/autonomy/proposals/:id/approve');
/** POST /api/atlas/autonomy/ceiling/proposals/:id/approve: the platform ceiling to FULL. */
export const ceilingProposalApprove = route<{ approved?: boolean; status?: string; ceiling?: AutonomyLevelName }, void, { reason?: string }>()(
  'POST', '/api/atlas/autonomy/ceiling/proposals/:id/approve');

export interface AutonomyCeiling {
  ceiling: AutonomyLevelName;
  isCapOff: boolean;
  levels: AutonomyLevelName[];
  note: string;
}
/** GET /api/atlas/autonomy/ceiling: Aithora's operators only. */
export const autonomyCeiling = route<AutonomyCeiling>()('GET', '/api/atlas/autonomy/ceiling');
/**
 * PUT /api/atlas/autonomy/ceiling {level, reason, confirm}: lowering is the operator tier;
 * raising is the owner's; raising to FULL answers 202 pending a second signer.
 */
export const autonomyCeilingSet = route<
  { ceiling?: AutonomyLevelName; previous?: AutonomyLevelName; changed?: boolean; pendingApproval?: boolean; proposalId?: string; message?: string },
  void,
  { level: AutonomyLevelName; reason?: string; confirm: true }
>()('PUT', '/api/atlas/autonomy/ceiling', { acceptStatuses: [202] });

/** A Guardian freeze that is engaged (guardian-freeze.ts listEngagedFreezes). */
export interface EngagedFreeze {
  scope: 'org' | 'global';
  organizationId: string | null;
  organizationName: string | null;
  engagedBy: string | null;
  engagedAt: string | null;
  reason: string | null;
}
/** An active governance kill switch (core/types.ts KillSwitch + its tenant). */
export interface ActiveKillSwitch {
  id: string;
  scope: string;
  scopeValue?: string;
  tenantId: string;
  tenantName: string;
  reason: string;
  activatedAt: string;
  activatedBy: string;
}
/** GET /api/stops: everything stopped right now. Operators see it; only the owner resumes. */
export const stopsList = route<{ freezes: EngagedFreeze[]; killSwitches: ActiveKillSwitch[]; canResume: boolean }>()('GET', '/api/stops');
/** POST /api/guardian/kill-switch {action:'release'}: resume one freeze (the owner only). */
export const freezeRelease = route<{ ok: boolean }, void, { action: 'release'; scope: 'org' | 'global'; organizationId?: string; confirm: true }>()(
  'POST', '/api/guardian/kill-switch');

// ── People (router.ts /admin/users/:id/logout, /admin/access-requests/*) ────

/** POST /admin/users/:id/logout: end every session the person has. */
export const adminUserLogout = route<{ success: boolean; sessionsKilled: number }>()('POST', `${A}/admin/users/:id/logout`);

/** POST /admin/access-requests/:id/approve: the operator org and its owner's invite link. */
export const accessRequestApprove = route<{ ok: boolean; organizationId: string; inviteUrl: string; pendingApproval?: boolean }>()(
  'POST', `${A}/admin/access-requests/:id/approve`);
/** POST /admin/access-requests/:id/deny */
export const accessRequestDeny = route<{ ok: boolean }>()('POST', `${A}/admin/access-requests/:id/deny`);

/** One building or operator (router.ts GET /admin/facilities). */
export interface AdminFacility {
  id: string;
  name: string;
  org_type: 'facility' | 'operator';
  status: string;
  parent_org_id: string | null;
  ccn: string | null;
  created_at: string;
  bed_count: number | null;
  pilot_start_date: string | null;
  pilot_days: number | null;
  pilot_end_date: string | null;
  /** Where the building's BAA stands: none, sent, signed, countersigned (null before any is recorded). */
  baa_status: string | null;
  residents: number;
  staff: number;
}
/** GET /admin/facilities: every building for Aithora; a facility admin's own. */
export const adminFacilities = route<{ facilities: AdminFacility[] }>()('GET', `${A}/admin/facilities`);

/** POST /admin/facilities: the onboarding wizard. A building from the catalog and its first admin's invite. */
export interface FacilityOnboardBody {
  name: string;
  ccn?: string;
  bedCount?: number;
  adminEmail?: string;
  adminName?: string;
  parentOrgId?: string;
}
export interface FacilityOnboardResult {
  ok: boolean;
  organizationId: string;
  inviteUrl?: string;
  adminInvitePendingApproval?: boolean;
  pilot?: { days: number; code: string | null; endsOn: string };
}
export const facilityOnboard = route<FacilityOnboardResult, void, FacilityOnboardBody>()('POST', `${A}/admin/facilities`);

// ── Family link requests (router.ts /family/requests*) ──────────────────────

export interface FamilyLinkRequestRow {
  id: string;
  family_user_id: string;
  family_name: string | null;
  family_email: string | null;
  created_at: string;
}
/** GET /family/requests?residentId=&organizationId=: pending requests for one resident. */
export const familyRequests = route<{ requests: FamilyLinkRequestRow[] }, { residentId: string; organizationId: string }>()('GET', `${A}/family/requests`);
export type FamilyRelationship = 'poa' | 'family' | 'emergency';
/** POST /family/requests/:id/decide: approve (with the relationship and what they may see) or decline. */
export const familyRequestDecide = route<
  { ok: boolean },
  void,
  { organizationId: string; approve: boolean; relationship?: FamilyRelationship; toggles?: Record<string, boolean> }
>()('POST', `${A}/family/requests/:id/decide`);

// ── Signed-out pages (router.ts /access-request, /invite/:token) ────────────

export interface AccessRequestBody {
  orgName: string;
  contactName: string;
  email: string;
  phone?: string;
  facilityCount?: number;
  roleWanted?: 'operator' | 'facility';
  note?: string;
  pilotCode?: string;
}
/** POST /access-request: public. Lands in Aithora's queue. */
export const accessRequestSubmit = route<{ ok: boolean; id: string; pilotCode?: string }, void, AccessRequestBody>()(
  'POST', `${A}/access-request`, { anonymous: true });

export interface InviteLookup {
  valid: boolean;
  email?: string;
  name?: string | null;
  role?: string;
  orgName?: string;
  username?: string | null;
  pendingApproval?: boolean;
  error?: string;
}
/** GET /invite/:token: public. Whether the link still works, and for whom. */
export const inviteLookup = route<InviteLookup>()('GET', `${A}/invite/:token`, { anonymous: true, acceptStatuses: [404] });
/**
 * POST /invite/:token/accept {password, username?}: public. The person's own password, and
 * a session. A role that must use two-factor gets no session yet: `twofaRequired` with an
 * enroll-mode `pendingToken`, which sign-in's enrolment step redeems for the session.
 */
export const inviteAccept = route<
  {
    ok: boolean; userId: string; name: string | null; role: string; organizationId: string; expiresAt: string;
    token?: string; twofaRequired?: boolean; twofaMode?: 'enroll'; pendingToken?: string;
  },
  void,
  { password: string; username?: string }
>()('POST', `${A}/invite/:token/accept`, { anonymous: true });

// ── System Pulse incidents (src/aithora/pulse/router.ts) ────────────────────

/** POST /api/pulse/incidents/:id/resolve {note}: a person closes an incident by hand. */
/** POST /api/pulse/incidents/:id/notes {note, kind}: one more entry on the append-only postmortem trail. */

// ── Threats (server.ts /api/threats) ────────────────────────────────────────

/** A detected threat (security/threat.ts). */
export interface ActiveThreat {
  id: string;
  tenantId: string;
  type: 'brute_force' | 'anomaly' | 'geo_violation' | 'suspicious_pattern' | 'velocity_abuse';
  severity: 'low' | 'medium' | 'high' | 'critical';
  actorId: string;
  description: string;
  evidence: Record<string, unknown>;
  status: 'active' | 'mitigated' | 'false_positive';
  detectedAt: string;
  mitigatedAt?: string;
}
/** GET /api/threats: the tenant's active threats (and all, and counts). */
export const threatsList = route<{ active: ActiveThreat[]; all: ActiveThreat[]; stats: unknown }>()('GET', '/api/threats');
/** POST /api/threats/:id/mitigate: stored, then sealed to the ledger. */
export const threatMitigate = route<{ success: boolean }>()('POST', '/api/threats/:id/mitigate');
/** POST /api/threats/:id/false-positive */
export const threatFalsePositive = route<{ success: boolean }>()('POST', '/api/threats/:id/false-positive');

// ── Out-of-band confirmation and alert history (server.ts /api/oob/*, /api/notifications) ──
// Governance-tenant data: Aithora's operator tier or a governed system's own key.

/** One pending out-of-band confirmation (core/oob.ts getPending). The code is never returned. */
export interface OobPending {
  id: string;
  actionId: string;
  actionSummary: string;
  channel: 'sms' | 'email' | string;
  /** Epoch milliseconds. */
  expiresAt: number;
}
/** GET /api/oob/pending */
export const oobPending = route<{ pending: OobPending[] }>()('GET', '/api/oob/pending');
/** POST /api/oob/verify {requestId, otp}: a wrong code leaves the action not run. */
export const oobVerify = route<{ valid: boolean; reason: string; actionId?: string }, void, { requestId: string; otp: string }>()('POST', '/api/oob/verify');
/** POST /api/oob/request: a code sent only to a contact the tenant registered. */
export const oobRequest = route<{ success: boolean; requestId?: string; expiresAt?: number }, void, { actionId: string; actionSummary: string; channel: 'sms' | 'email'; recipient: string }>()(
  'POST', '/api/oob/request');

/** One outbound alert, as sent (core/notifications.ts). Severity: 0 info, 1 warn, 2 critical, 3 kill. */
export interface SentAlert {
  id: string;
  tenantId: string;
  severity: number;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  channel: 'webhook' | 'email' | 'sms' | 'dashboard';
  sentAt: string;
  delivered: boolean;
  error?: string;
}
/** GET /api/notifications: the outbound alert history and its counts. */
export const alertHistory = route<{ notifications: SentAlert[]; stats: { total: number; bySeverity: Record<string, number>; deliveryRate: number } }>()(
  'GET', '/api/notifications');

// ── Two-signature approvals (router.ts /signoffs, v3/oversight/signoffs.ts) ──
// Andrew, 2026-09-30: a building's own clinicians sign risky actions; a clock per item;
// unsigned at the clock it climbs the alert ladder; still unsigned it stays blocked.

export type SignoffSeverity = 'high' | 'medium' | 'low';
export interface SignoffItem {
  id: string;
  kind: string;
  act: string;
  severity: SignoffSeverity;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  have: number;
  need: number;
  /** The signatures reached the number required (only signing approves). */
  ready: boolean;
  signers: Array<{ name: string | null; title: string | null; at: string | null }>;
  rejectedBy: { name: string | null; reason: string | null; at: string | null } | null;
  clock: {
    expiresAt: string | null;
    step: number;
    stepLabel: string | null;
    escalated: boolean;
    ladderExhausted: boolean;
    hardExpiresAt: string | null;
  };
  /** Atlas's read, only when the action came with one; never invented. */
  read: { confidence: number | null; forIt: string | null; against: string | null } | null;
  needsReason: boolean;
  requestedAt: string | null;
  requestReason: string | null;
  facility: { id: string; name: string | null };
  ledgerRef: string | null;
  mine: boolean;
  isRequester: boolean;
  canSign: boolean;
  canCollect: boolean;
}
export interface SignoffList {
  real: true;
  items: SignoffItem[];
  canSign: boolean;
  signerNote: string | null;
  rules: { stepMinutes: Record<SignoffSeverity, number>; lastStep: number; hardExpiryHours: number };
}
/** GET /signoffs: the building's list (Aithora: every building, read-only). */
export const signoffsList = route<SignoffList, { status?: 'pending' | 'all' }>()('GET', `${A}/signoffs`);
/** POST /signoffs: a building's staff asks for a two-signature action. */
export const signoffRequest = route<{ real: true; id: string; ledgerRef: string }, void, { kind: 'fhir_export'; residentId: string; destination: string; reason: string }>()(
  'POST', `${A}/signoffs`);
/** POST /signoffs/:id/sign {reason}: a clinician of the building signs (reason required on high). */
export const signoffSign = route<{ real: true; ledgerRef: string; item: SignoffItem }, void, { reason?: string }>()('POST', `${A}/signoffs/:id/sign`);
/** POST /signoffs/:id/reject {reason}: one clinician stops it. */
export const signoffReject = route<{ real: true; ledgerRef: string }, void, { reason: string }>()('POST', `${A}/signoffs/:id/reject`);
/** POST /signoffs/:id/release: the requester collects what the approval unlocked, once. */
export const signoffRelease = route<{ real: true; destination: string | null; bundle: unknown }>()('POST', `${A}/signoffs/:id/release`);

// ── Custom roles per building (router.ts /admin/facilities/:orgId/roles) ────
// Restored from the Flutter Facilities page: a named role is a base tier plus a bounded
// set of extra capabilities. Assigning one goes through PUT /admin/users/:id {customRoleId}.

export interface CustomRole {
  id: string;
  name: string;
  base_role: 'CAREGIVER' | 'STAFF' | 'ADMIN';
  added_caps: string[];
  created_at: string;
}
export interface CustomRolesResponse {
  roles: CustomRole[];
  baseTiers: Array<'CAREGIVER' | 'STAFF' | 'ADMIN'>;
  extraCaps: Array<{ id: string; label: string }>;
}
/** GET /admin/facilities/:orgId/roles: Aithora any building; a Facility Admin their own. */
export const customRolesList = route<CustomRolesResponse>()('GET', `${A}/admin/facilities/:orgId/roles`);
/** POST /admin/facilities/:orgId/roles {name, baseRole, addedCaps}: audited. */
export const customRoleCreate = route<{ ok: boolean; role: CustomRole }, void, { name: string; baseRole: string; addedCaps: string[] }>()(
  'POST', `${A}/admin/facilities/:orgId/roles`);
/** DELETE /admin/facilities/:orgId/roles/:roleId: anyone holding it keeps their base role. */
export const customRoleDelete = route<{ ok: boolean; unassigned: number }>()('DELETE', `${A}/admin/facilities/:orgId/roles/:roleId`);

// ── Aithora Home households (router.ts POST /admin/households) ──────────────

export interface HouseholdCreateBody {
  householdName: string;
  seniorName: string;
  seniorEmail?: string;
  familyName?: string;
  familyEmail?: string;
}
/** POST /admin/households: Aithora only. The senior's account, and a family invite when an email is given. */
export const householdCreate = route<{ ok: boolean; organizationId: string; memberUserId: string; inviteToken?: string; inviteUrl?: string; /** The senior's own sign-in link (when their email was given). */ seniorInviteToken?: string; seniorInviteUrl?: string }, void, HouseholdCreateBody>()(
  'POST', `${A}/admin/households`);

// ── Sign-in: SSO providers (router.ts /auth/oauth/*) ────────────────────────

/** GET /auth/oauth/providers: public. Which single sign-on providers are switched on. */
export const ssoProviders = route<{ providers: Array<{ id: string; label: string }> }>()('GET', `${A}/auth/oauth/providers`, { anonymous: true });
/** Where the browser goes to start single sign-on (the server redirects to the provider). */
export const ssoStartPath = (provider: string) => `${A}/auth/oauth/${encodeURIComponent(provider)}/start`;

/** POST /auth/change-password with a reset link's token: sets the password, signs nobody in. */
export const resetPasswordWithLink = route<{ ok: boolean; passwordSet: true; signInNext: true }, void, { changeToken: string; newPassword: string }>()(
  'POST', `${A}/auth/change-password`, { anonymous: true });
